import { Store } from '@/class/Store';

export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';

export const REMEMBER_ME_DAYS = 30;

export const AUTH_KEYS = {
  isAuthenticated: 'isAuthenticated',
  userRole: 'userRole',
  store: 'store',
  token: 'authToken',
  rememberMe: 'rememberMe',
  expiresAt: 'authExpiresAt',
} as const;

export const clearAuthStorage = (storage: Storage): void => {
  storage.removeItem(AUTH_KEYS.isAuthenticated);
  storage.removeItem(AUTH_KEYS.userRole);
  storage.removeItem(AUTH_KEYS.store);
  storage.removeItem(AUTH_KEYS.token);
  storage.removeItem(AUTH_KEYS.rememberMe);
  storage.removeItem(AUTH_KEYS.expiresAt);
};

export const clearAllAuthStorage = (): void => {
  clearAuthStorage(sessionStorage);
  clearAuthStorage(localStorage);
};

export const getActiveStorage = (): Storage | null => {
  if (localStorage.getItem(AUTH_KEYS.rememberMe) === 'true') {
    const expiresAt = localStorage.getItem(AUTH_KEYS.expiresAt);
    if (expiresAt && Date.now() > Number(expiresAt)) {
      clearAuthStorage(localStorage);
      return null;
    }

    return localStorage;
  }

  if (sessionStorage.getItem(AUTH_KEYS.isAuthenticated) === 'true') {
    return sessionStorage;
  }

  return null;
};

export const getAuthToken = (): string | null => {
  const storage = getActiveStorage();
  if (!storage) {
    return null;
  }

  return storage.getItem(AUTH_KEYS.token);
};

export const loadAuthState = (): {
  isAuthenticated: boolean;
  userRole: 'store' | 'system' | null;
  store: Store;
} => {
  const storage = getActiveStorage();
  if (!storage) {
    return { isAuthenticated: false, userRole: null, store: {} as Store };
  }

  const storedRole = storage.getItem(AUTH_KEYS.userRole);
  const storedJson = storage.getItem(AUTH_KEYS.store);

  return {
    isAuthenticated: storage.getItem(AUTH_KEYS.isAuthenticated) === 'true',
    userRole: storedRole === 'system' || storedRole === 'store' ? storedRole : null,
    store: storedJson ? JSON.parse(storedJson) : ({} as Store),
  };
};

export const persistAuthState = (
  storage: Storage,
  rememberMe: boolean,
  role: 'store' | 'system',
  storeData: Store,
  token?: string | null
): void => {
  storage.setItem(AUTH_KEYS.isAuthenticated, 'true');
  storage.setItem(AUTH_KEYS.userRole, role);

  if (role === 'store') {
    storage.setItem(AUTH_KEYS.store, JSON.stringify(storeData));
    if (token) {
      storage.setItem(AUTH_KEYS.token, token);
    } else {
      storage.removeItem(AUTH_KEYS.token);
    }
  } else if (role === 'system') {
    storage.removeItem(AUTH_KEYS.store);
    if (token) {
      storage.setItem(AUTH_KEYS.token, token);
    } else {
      storage.removeItem(AUTH_KEYS.token);
    }
  } else {
    storage.removeItem(AUTH_KEYS.store);
    storage.removeItem(AUTH_KEYS.token);
  }

  if (rememberMe) {
    storage.setItem(AUTH_KEYS.rememberMe, 'true');
    storage.setItem(
      AUTH_KEYS.expiresAt,
      String(Date.now() + REMEMBER_ME_DAYS * 24 * 60 * 60 * 1000)
    );
  }
};

export const updateStoredStore = (storeData: Store): void => {
  const storage = getActiveStorage();
  if (storage && storage.getItem(AUTH_KEYS.userRole) === 'store') {
    storage.setItem(AUTH_KEYS.store, JSON.stringify(storeData));
  }
};

export const notifyUnauthorized = (): void => {
  clearAllAuthStorage();
  window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
};
